'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowRight, CheckCircle2, Gift } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import type { OccasionPackage } from '@/data/mockData';
import { fetchLiveHampers } from '@/services/api';
import { useCommerce } from '@/context/CommerceContext';

export default function FestivalsPage() {
  const router = useRouter();
  const { hampers } = useCommerce();

  // Filter context hampers for festivals if present, else empty
  const initialFestivals = React.useMemo(() => {
    if (!hampers || hampers.length === 0) return [];
    return hampers.filter(
      (h) =>
        h.moduleType === 'festivals' ||
        ['festivals', 'diwali', 'pongal', 'puthandu', 'navaratri', 'krishna-jayanthi'].includes(h.occasionCategory)
    );
  }, [hampers]);

  const [festivalsList, setFestivalsList] = React.useState<OccasionPackage[]>(initialFestivals);
  const [loading, setLoading] = React.useState<boolean>(initialFestivals.length === 0);

  React.useEffect(() => {
    let isMounted = true;
    fetchLiveHampers('festivals')
      .then((data) => {
        if (isMounted) {
          setFestivalsList(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.warn('Failed to load live festival hampers:', err);
        if (isMounted) {
          setFestivalsList(initialFestivals || []);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [initialFestivals]);

  const categories = [
    {
      title: 'Deepavali & Diwali',
      subtitle: 'Grand Festive Trunks',
      desc: 'Pure cow ghee sweets, spicy savouries & terracotta diyas in royal vermilion keepsake boxes.',
    },
    {
      title: 'Pongal & Sankranti',
      subtitle: 'Harvest Celebration Trays',
      desc: 'Authentic Sakkarai Pongal mixes, brass pots & traditional Chettinad village savouries.',
    },
    {
      title: 'Navaratri & Dussehra',
      subtitle: 'Golu & Thamboolam Gifts',
      desc: 'Pious prasadam boxes, sundal snacks & auspicious festive return favours for relatives.',
    },
    {
      title: 'New Year & Auspicious Days',
      subtitle: 'Heritage Gift Favors',
      desc: 'Traditional kani treats, Tirunelveli ghee halwa & crunchy hand-twisted Kai Murukku assortments.',
    },
  ];

  return (
    <main className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* 1. Festival Hero */}
      <section className="bg-[#FAFBF9] border-b border-neutral-200 py-12 sm:py-16">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20 text-center space-y-4">
          <div className="inline-flex items-center space-x-1.5 text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100/70 px-3.5 py-1 rounded-full">
            <Gift className="w-3.5 h-3.5 text-emerald-700" />
            <span>Curated Festival Collections</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold text-neutral-900 uppercase tracking-tight">
            MADE FOR EVERY FESTIVAL CELEBRATION
          </h1>
          <p className="text-sm sm:text-base text-neutral-600 max-w-2xl mx-auto font-medium leading-relaxed">
            South Indian festivals are incomplete without authentic crunch. Handcrafted sweets &amp; savouries, 
            royal packaging, and celebratory gifting for Deepavali, Pongal, Navaratri, and auspicious festival gatherings.
          </p>
        </div>
      </section>

      {/* 2. Festival Categories Grid */}
      <section className="py-12 bg-white border-b border-neutral-100">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((c, i) => (
              <div
                key={i}
                className="p-5 rounded-2xl bg-[#FAFBF9] border border-neutral-200/80 hover:border-emerald-600/50 hover:shadow-md transition-all"
              >
                <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider block mb-1">
                  {c.subtitle}
                </span>
                <h3 className="text-base font-bold text-neutral-900 mb-2">
                  {c.title}
                </h3>
                <p className="text-xs text-neutral-600 leading-relaxed font-medium">
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Featured Festival Packages */}
      <section className="py-14 bg-white flex-1">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20 space-y-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-neutral-900 uppercase tracking-tight">
                PRE-CURATED FESTIVAL PACKAGES
              </h2>
              <div className="h-0.5 w-12 bg-emerald-600 rounded-full mt-1.5"></div>
            </div>
            <Link href="/custom-package" className="text-xs sm:text-sm font-bold text-emerald-700 hover:underline">
              Or Build Your Own Custom Box →
            </Link>
          </div>

          {loading && festivalsList.length === 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  className="rounded-3xl bg-neutral-50/80 border border-neutral-200/80 p-6 flex flex-col justify-between animate-pulse min-h-[320px]"
                >
                  <div className="space-y-3">
                    <div className="h-4 bg-neutral-200 rounded w-1/4"></div>
                    <div className="h-6 bg-neutral-200 rounded w-3/4"></div>
                    <div className="h-4 bg-neutral-200 rounded w-full"></div>
                    <div className="space-y-2 pt-4">
                      <div className="h-3.5 bg-neutral-200 rounded w-2/3"></div>
                      <div className="h-3.5 bg-neutral-200 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-6 border-t border-neutral-200/60">
                    <div className="h-6 bg-neutral-200 rounded w-24"></div>
                    <div className="h-10 bg-neutral-200 rounded-xl w-32"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : festivalsList.length === 0 ? (
            <div className="text-center py-16 bg-[#FAFBF9] rounded-3xl border border-neutral-200/80 p-8 space-y-4">
              <Gift className="w-10 h-10 text-emerald-700 mx-auto" />
              <h3 className="text-lg font-bold text-neutral-800">No festival hampers currently scheduled</h3>
              <p className="text-xs text-neutral-500 max-w-md mx-auto">
                Explore building your custom festive box or inquire with our concierge for upcoming celebrations.
              </p>
              <Link
                href="/custom-package"
                className="inline-block px-5 py-2.5 rounded-full bg-emerald-800 text-white text-xs font-bold uppercase tracking-wider"
              >
                Build Custom Box
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {festivalsList.map((hamper) => {
                const snacksList = (
                  hamper.includedProducts && hamper.includedProducts.length > 0
                    ? hamper.includedProducts.map((p: any) =>
                        `${p.quantity > 1 ? `${p.quantity}x ` : ''}${p.name}${p.weight ? ` (${p.weight})` : ''}`
                      )
                    : hamper.includedSnacks || []
                );

                return (
                  <div
                    key={hamper.id || hamper._id}
                    className="flex flex-col-reverse sm:flex-row items-stretch justify-between rounded-3xl bg-white border border-neutral-200/80 overflow-hidden hover:shadow-xl hover:border-emerald-600/40 transition-all duration-300 group"
                  >
                    {/* LEFT SIDE: Details */}
                    <div className="flex-1 p-6 sm:p-7 flex flex-col justify-between space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                            {hamper.occasion || hamper.occasionCategory || 'Festival Celebrations'}
                          </span>
                        </div>

                        <Link
                          href={`/festivals/${hamper.slug || hamper.id}`}
                          className="hover:text-emerald-700 transition-colors"
                        >
                          <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 leading-snug">
                            {hamper.title}
                          </h3>
                        </Link>

                        <p className="text-xs text-neutral-600 mt-2 leading-relaxed font-medium">
                          {hamper.subtitle}
                        </p>
                      </div>

                      <div className="space-y-1.5 py-3 border-t border-b border-neutral-100">
                        <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block">
                          Included Traditional Snacks:
                        </span>
                        <ul className="space-y-1.5 text-xs text-neutral-700 font-medium">
                          {snacksList.map((s, i) => (
                            <li key={i} className="flex items-center space-x-1.5">
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                              <span className="line-clamp-1">{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="pt-1 flex items-center justify-between gap-3">
                        <div>
                          <span className="text-lg font-bold text-neutral-900">From ₹{hamper.basePrice}</span>
                          <span className="text-[10px] text-neutral-400 block font-medium">
                            Min. {hamper.minOrderQty || 10} boxes
                          </span>
                        </div>

                        <Link
                          href={`/festivals/${hamper.slug || hamper.id}`}
                          className="py-2.5 px-4 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer shadow-xs whitespace-nowrap inline-flex items-center space-x-1"
                        >
                          <span>Inquire / Quote</span>
                          <ArrowRight className="w-3.5 h-3.5 ml-0.5" />
                        </Link>
                      </div>
                    </div>

                    {/* RIGHT SIDE: Image */}
                    <Link
                      href={`/festivals/${hamper.slug || hamper.id}`}
                      className="relative w-full sm:w-[42%] md:w-[44%] aspect-[4/3] sm:aspect-auto min-h-[260px] sm:min-h-[340px] bg-neutral-100 overflow-hidden shrink-0 block"
                    >
                      <Image
                        src={hamper.image || '/images/royal_gift_box.jpg'}
                        alt={hamper.title || 'Festive Hamper'}
                        fill
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 30vw"
                        unoptimized
                        className="object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      />
                      <span className="absolute top-3.5 right-3.5 text-[10px] sm:text-[11px] font-bold text-white bg-emerald-800/90 backdrop-blur-xs px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                        {hamper.badge || 'Festive Special'}
                      </span>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* 4. Custom Festival CTA */}
      <section className="bg-[#0A261A] text-white py-14">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20 text-center space-y-4">
          <h2 className="text-2xl sm:text-4xl font-semibold uppercase tracking-tight">
            Planning a Festive Gathering with 100+ Boxes?
          </h2>
          <p className="text-xs sm:text-sm text-neutral-300 max-w-xl mx-auto font-medium leading-relaxed">
            Let our celebration specialists handle bespoke festival packaging, personalized blessing cards, 
            and doorstep delivery before the festival.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <Link
              href="/custom-package"
              className="px-6 py-3 rounded-full bg-[#F5B52E] hover:bg-[#ffc34d] text-[#0A261A] font-bold text-xs sm:text-sm uppercase tracking-wider transition-all"
            >
              Open Custom Box Builder
            </Link>
            <Link
              href="/contact"
              className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-xs sm:text-sm uppercase tracking-wider transition-all"
            >
              Speak to Gifting Desk
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
